import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR } from "../lib/format";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";

export default function EngineeringMilestone() {
  const { projectsQS } = usePeriod();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/kpi/expected-cashflow?${projectsQS.replace(/^&/, "")}`).then((r) => setData(r.data));
  }, [projectsQS]);

  if (!data) return <><Header title="Engineering Milestone" /><div className="p-6">Loading...</div></>;

  return (
    <>
      <Header title="Engineering Milestone" subtitle="Construction Progress · Cashflow Forecast" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <AIInsightBanner context={{ kpis: {} }} />

        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold mb-3">Expected Monthly Collection · Construction-driven</div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data.monthly_forecast}>
              <defs>
                <linearGradient id="fcg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F1D32" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#0F1D32" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
              <Area dataKey="expected" stroke="#0F1D32" fill="url(#fcg)" strokeWidth={2} name="Expected Collection" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Schedule Deviation Distribution</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.projects}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="project_name" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="avg_deviation_days" fill="#D4AF37" radius={[4, 4, 0, 0]} name="Avg Deviation (days)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Project Health Score</div>
            <div className="space-y-3 max-h-[260px] overflow-y-auto scroll-thin pr-2">
              {data.projects.map((p) => (
                <div key={p.project_name}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="truncate font-medium">{p.project_name}</span>
                    <span className="font-mono font-bold">{p.health_score}/10</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full" style={{
                      width: `${p.health_score * 10}%`,
                      background: p.health_score > 7 ? "#059669" : p.health_score > 4 ? "#D97706" : "#DC2626"
                    }} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">
                    {p.completion_pct}% complete · {p.completed_stages}/{p.total_stages} stages · {p.avg_deviation_days > 0 ? "+" : ""}{p.avg_deviation_days}d deviation
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {data.projects.map((p) => (
          <div key={p.project_name} className="bg-white rounded-md border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold">{p.project_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                Health {p.health_score}/10 · {p.completion_pct}% complete · Expected remaining {fmtINR(p.expected_remaining, { short: true })}
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Stage</th>
                  <th className="text-left px-4 py-2">Revised Date</th>
                  <th className="text-left px-4 py-2">Completed Date</th>
                  <th className="text-right px-4 py-2">Deviation (d)</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {p.stages.map((s, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-2">{s.event}</td>
                    <td className="px-4 py-2 font-mono">{new Date(s.revised_date).toLocaleDateString()}</td>
                    <td className="px-4 py-2 font-mono">{s.completed_date ? new Date(s.completed_date).toLocaleDateString() : "—"}</td>
                    <td className={`numeric-cell px-4 py-2 ${s.deviation_days > 7 ? "text-red-600" : s.deviation_days > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                      {s.deviation_days > 0 ? "+" : ""}{s.deviation_days}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono uppercase ${
                        s.completed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                      }`}>{s.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </>
  );
}
